/**
 * Friend leagues: small private groups, joined with an invite code, ranked by
 * XP earned this week. Needs an account; members see only nicknames, weekly XP
 * and streaks (see supabase/schema.sql).
 */

import { el } from '../utils/dom.js';
import { icon } from '../utils/icons.js';
import { formatNumber, plural } from '../utils/format.js';
import { refresh } from '../app/router.js';
import { emit, on, EVENTS } from '../app/eventBus.js';
import { button, emptyState, linkButton, sectionHead } from '../components/ui.js';
import { confirmDialog, openModal } from '../components/Modal.js';
import { isSupabaseConfigured } from '../lib/supabaseClient.js';
import { getSession } from '../services/authService.js';
import { syncNow } from '../services/syncService.js';
import {
  createLeague,
  getNickname,
  getStandings,
  joinLeague,
  leaveLeague,
  listLeagues,
  setNickname
} from '../services/leagueService.js';
import {
  checkLeagueName,
  checkNickname,
  formatInviteCode,
  inviteLink,
  leagueWeek,
  normalizeInviteCode,
  rankStandings
} from '../services/leagueCore.js';

const toast = (title, variant = 'success', body) => emit(EVENTS.toast, { title, variant, body });

function pageHead(subtitle) {
  return el('div.page-head', {}, [
    el('div.page-head__text', {}, [el('h1', { text: 'Leagues' }), el('p', { text: subtitle })])
  ]);
}

function textInput({ placeholder, value = '', label, maxLength, onEnter }) {
  const input = el('input', {
    class: 'input input--text',
    type: 'text',
    placeholder,
    value,
    maxLength,
    'aria-label': label,
    on: { keydown: (event) => event.key === 'Enter' && onEnter?.() }
  });
  return input;
}

/** A single-field form with inline error text; `action(value)` may throw a learner-friendly error. */
function inlineForm({ label, placeholder, value, maxLength, submitLabel, variant = 'primary', action }) {
  const error = el('p.text-sm.league-form__error');
  let busy = false;
  const submit = async () => {
    if (busy) return;
    busy = true;
    error.textContent = '';
    try {
      await action(input.value);
    } catch (err) {
      error.textContent = err?.message || 'Something went wrong. Try again.';
    } finally {
      busy = false;
    }
  };
  const input = textInput({ placeholder, value, label, maxLength, onEnter: submit });
  return el('div.league-form', {}, [
    el('label.league-form__label', { text: label }),
    el('div.league-form__row', {}, [input, button(submitLabel, { variant, onClick: submit })]),
    error
  ]);
}

function nicknameCard() {
  return el('div.card.stack', {}, [
    el('div.card__title', { text: 'Choose a nickname' }),
    el('p.text-sm.text-muted', {
      text: 'This is how you appear to people in your leagues, next to your weekly XP and streak. Nobody sees your email.'
    }),
    inlineForm({
      label: 'Nickname',
      placeholder: 'e.g. Asha K',
      maxLength: 24,
      submitLabel: 'Save nickname',
      action: async (value) => {
        const { clean, error } = checkNickname(value);
        if (error) throw new Error(error);
        await setNickname(clean);
        refresh();
      }
    })
  ]);
}

function changeNickname(current) {
  const modal = openModal({
    title: 'Change nickname',
    body: inlineForm({
      label: 'Nickname',
      value: current,
      maxLength: 24,
      submitLabel: 'Save',
      action: async (value) => {
        const { clean, error } = checkNickname(value);
        if (error) throw new Error(error);
        await setNickname(clean);
        modal.close();
        toast('Nickname updated');
        refresh();
      }
    })
  });
}

async function shareInvite(league) {
  const link = inviteLink(league.invite_code);
  const text = `Join my QuantMaster league "${league.name}": code ${formatInviteCode(league.invite_code)}`;
  try {
    if (navigator.share) {
      await navigator.share({ title: 'QuantMaster league', text, url: link });
      return;
    }
    await navigator.clipboard.writeText(`${text}\n${link}`);
    toast('Invite copied', 'success', 'Paste it to your friends.');
  } catch (error) {
    if (error?.name !== 'AbortError') toast('Could not share the invite', 'warning', `The code is ${formatInviteCode(league.invite_code)}.`);
  }
}

function standingsList(rows) {
  if (rows.error) return el('p.text-sm.text-muted', { text: rows.error });
  const ranked = rankStandings(rows);
  return el(
    'ol.standings',
    {},
    ranked.map((row) =>
      el('li', { class: `standings__row${row.is_me ? ' is-me' : ''}${row.rank <= 3 && row.weekly_xp > 0 ? ` is-top is-top-${row.rank}` : ''}` }, [
        el('span.standings__rank', { text: String(row.rank) }),
        el('span.standings__name', { text: row.is_me ? `${row.display_name} (you)` : row.display_name }),
        row.streak > 0
          ? el('span.standings__streak', { title: `${row.streak}-day streak` }, [icon('flame', { size: 13, fill: 'currentColor', strokeWidth: 0 }), String(row.streak)])
          : el('span.standings__streak'),
        el('span.standings__xp', { text: `${formatNumber(row.weekly_xp)} XP` })
      ])
    )
  );
}

function leagueCard(league, rows) {
  return el('div.card.league-card', {}, [
    el('div.row', { style: { marginBottom: '12px' } }, [
      el('div', { style: { flex: 1, minWidth: 0 } }, [
        el('div.card__title', { text: league.name }),
        el('div.card__sub', { text: plural(league.member_count, 'member') })
      ]),
      icon('trophy', { size: 18, className: 'text-subtle' })
    ]),
    standingsList(rows),
    el('div.league-card__foot', {}, [
      el('span.text-sm.text-muted', {}, ['Invite code ', el('strong.league-code', { text: formatInviteCode(league.invite_code) })]),
      el('div.row', { style: { gap: '8px', marginLeft: 'auto' } }, [
        button('Share invite', { variant: 'soft', size: 'sm', onClick: () => shareInvite(league) }),
        button('Leave', {
          variant: 'ghost',
          size: 'sm',
          onClick: () =>
            confirmDialog({
              title: `Leave "${league.name}"?`,
              message: 'You can rejoin later with the invite code. If you are the last member, the league is deleted.',
              confirmLabel: 'Leave league',
              danger: true,
              onConfirm: async () => {
                try {
                  await leaveLeague(league.id);
                  toast('You left the league', 'info');
                } catch (error) {
                  toast('Could not leave the league', 'warning', error.message);
                }
                refresh();
              }
            })
        })
      ])
    ])
  ]);
}

function createAndJoin(prefillCode) {
  return el('div.grid.grid--2', { style: { gap: '16px' } }, [
    el('div.card', {}, [
      inlineForm({
        label: 'Start a league',
        placeholder: 'e.g. Class of 2026',
        maxLength: 40,
        submitLabel: 'Create',
        action: async (value) => {
          const { clean, error } = checkLeagueName(value);
          if (error) throw new Error(error);
          const league = await createLeague(clean);
          toast('League created', 'success', `Share the code ${formatInviteCode(league.invite_code)} with your friends.`);
          refresh();
        }
      })
    ]),
    el('div.card', {}, [
      inlineForm({
        label: 'Join with an invite code',
        placeholder: 'e.g. QM7K-4RTX',
        value: prefillCode ? formatInviteCode(prefillCode) : '',
        maxLength: 12,
        submitLabel: 'Join',
        variant: 'soft',
        action: async (value) => {
          const league = await joinLeague(value);
          toast(`Joined "${league.name}"`);
          window.history.replaceState(null, '', `${window.location.pathname}#/leagues`);
          refresh();
        }
      })
    ])
  ]);
}

export async function LeaguesView(ctx) {
  const week = leagueWeek();
  const subtitle = `This week: ${week.label} · ${plural(week.daysLeft, 'day')} left · resets on Monday (India time)`;
  const invited = normalizeInviteCode(ctx.query.join);

  if (!isSupabaseConfigured()) {
    return {
      title: 'Leagues',
      element: el('div', {}, [
        pageHead(subtitle),
        emptyState({ title: 'Leagues are not available here', body: 'They need accounts, which are not set up for this copy of QuantMaster.', iconName: 'trophy' })
      ])
    };
  }

  const cleanup = on(EVENTS.authChanged, () => refresh());
  const session = await getSession().catch(() => null);
  if (!session) {
    return {
      title: 'Leagues',
      cleanup,
      element: el('div', {}, [
        pageHead(subtitle),
        emptyState({
          title: 'Sign in to join a league',
          body:
            'Leagues rank you and your friends by XP earned this week. Create a free account or sign in from Settings (no email is sent).' +
            (invited ? ` Then come back here and enter the code ${formatInviteCode(invited)}.` : ''),
          iconName: 'trophy',
          action: linkButton('Go to Settings', '/settings', { variant: 'primary' })
        })
      ])
    };
  }

  let nickname;
  let leagues;
  try {
    [nickname, leagues] = await Promise.all([getNickname(), listLeagues()]);
  } catch (error) {
    return {
      title: 'Leagues',
      cleanup,
      element: el('div', {}, [
        pageHead(subtitle),
        emptyState({ title: 'Could not load your leagues', body: error.message, iconName: 'refresh', action: button('Try again', { variant: 'primary', onClick: () => refresh() }) })
      ])
    };
  }

  if (!nickname) {
    return { title: 'Leagues', cleanup, element: el('div', {}, [pageHead(subtitle), nicknameCard()]) };
  }

  // Push this device's latest XP once, then load every league's standings.
  await syncNow().catch(() => {});
  const standings = await Promise.all(
    leagues.map((league) => getStandings(league.id, { pushFirst: false }).catch((error) => ({ error: error.message })))
  );
  const alreadyIn = invited && leagues.some((l) => l.invite_code === invited);

  const element = el('div', {}, [
    pageHead(subtitle),
    el('p.text-sm.text-muted', { style: { marginTop: '-8px', marginBottom: '20px' } }, [
      'You appear as ',
      el('strong', { text: nickname }),
      '. ',
      el('a', { href: '#/leagues', on: { click: (event) => { event.preventDefault(); changeNickname(nickname); } }, text: 'Change' })
    ]),
    invited && !alreadyIn
      ? el('div.streak-note', { style: { marginBottom: '16px' } }, [
          el('span.streak-note__icon', {}, [icon('trophy', { size: 18 })]),
          el('div.streak-note__body', { text: `You have been invited to a league. Its code ${formatInviteCode(invited)} is filled in below: press Join.` })
        ])
      : null,
    leagues.length
      ? el('section.section', {}, [
          sectionHead('Your leagues', { hint: 'Ranked by XP earned this week' }),
          el('div.stack', { style: { gap: '16px' } }, leagues.map((league, i) => leagueCard(league, standings[i])))
        ])
      : el('section.section', {}, [
          emptyState({
            title: 'No leagues yet',
            body: 'Start one and share its invite code, or join a friend’s league with theirs. Everyone’s weekly XP is compared, and the table resets every Monday.',
            iconName: 'trophy'
          })
        ]),
    el('section.section', {}, [sectionHead('Start or join'), createAndJoin(alreadyIn ? null : invited)]),
    el('p.text-xs.text-subtle', {
      text: 'League members see only your nickname, your XP this week and your current streak. See Privacy for details.'
    })
  ]);

  return { title: 'Leagues', cleanup, element };
}
